import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Drawer, Button, Avatar, ScrollArea } from '@mantine/core';
import { useApiClient } from '../../api/ApiClientContext';
import { EndorsementDirection, EndorsementItemResponse } from '../../api/Api';
import dayjs from 'dayjs';
import styles from './EndorsementsDrawer.module.scss';

interface EndorsementsDrawerProps {
  opened: boolean;
  onClose: () => void;
  direction: EndorsementDirection;
}

const AVATAR_COLORS = ['#4C6EF5', '#FA5252', '#12B886', '#FAB005', '#7950F2'];

const EndorsementCard: React.FC<{ item: EndorsementItemResponse; direction: EndorsementDirection }> = ({
  item,
  direction,
}) => {
  const initials = item.person.name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const colorIndex = item.person.id.charCodeAt(0) % AVATAR_COLORS.length;

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <Avatar
          src={item.person.avatar_url}
          alt={item.person.name}
          size={40}
          radius="xl"
          color={AVATAR_COLORS[colorIndex]}
        >
          {initials}
        </Avatar>
        <div className={styles.personInfo}>
          <span className={styles.personName}>{item.person.name}</span>
          {item.person.role && <span className={styles.personRole}>{item.person.role}</span>}
        </div>
        <span className={styles.date}>{dayjs(item.endorsed_at).format('DD MMM, YYYY')}</span>
      </div>

      <div className={styles.section}>
        <span className={styles.sectionLabel}>HABIT</span>
        <span className={styles.sectionText}>{item.habit_name}</span>
      </div>

      <div className={styles.section}>
        <span className={styles.sectionLabel}>
          {direction === 'given' ? 'YOUR ENDORSEMENT' : 'ENDORSEMENT'}
        </span>
        <span className={styles.sectionText}>{item.endorsement_option_label}</span>
      </div>
    </div>
  );
};

export const EndorsementsDrawer: React.FC<EndorsementsDrawerProps> = ({ opened, onClose, direction }) => {
  const apiClient = useApiClient();

  const { data, isLoading } = useQuery({
    queryKey: ['insightsEndorsements', direction],
    queryFn: async () => {
      const res = await apiClient.api.getEndorsementsApiV1InsightsEndorsementsGet({
        direction,
        limit: 100,
      });
      return res?.data;
    },
    enabled: opened,
  });

  const title = direction === 'given' ? 'Endorsements given' : 'Endorsements received';

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={title}
      position="right"
      size="23rem"
      scrollAreaComponent={ScrollArea.Autosize}
      radius="lg"
      classNames={{
        title: styles.drawerTitle,
        body: styles.drawerBody,
        header: styles.drawerHeader,
      }}
    >
      <div className={styles.content}>
        {isLoading ? (
          <div className={styles.loadingState}>Loading...</div>
        ) : !data?.items?.length ? (
          <div className={styles.emptyState}>No endorsements yet</div>
        ) : (
          <>
            <span className={styles.countLabel}>
              {data.total_count} ENDORSEMENT{data.total_count !== 1 ? 'S' : ''}
            </span>
            <div className={styles.cardList}>
              {data.items.map((item) => (
                <EndorsementCard key={item.id} item={item} direction={direction} />
              ))}
            </div>
          </>
        )}
      </div>

      <div className={styles.footer}>
        <Button variant="default" radius={16} onClick={onClose} size="sm">
          Close
        </Button>
      </div>
    </Drawer>
  );
};

export default EndorsementsDrawer;
